import React, { useState } from 'react';
import { AritoFormV2, AritoIcon, BottomActionBarV2, BottomBarActionButtonV2, DialogV2 } from '@/components/custom/erp';
import { initialValues, SearchFormValues, searchSchema, SupplierGroupFormattedData } from '../../schemas';
import SupplierGroupForm from './SupplierGroupForm';
import { Group, GroupType } from '@/types/schemas';
import { FormMode } from '@/types/form';

interface SupplierGroupDialogProps {
  open: boolean;
  mode: FormMode;
  initialData?: {
    ma_nhom: string;
    ten_phan_nhom: string;
    ten2?: string | null;
    trang_thai: string | number;
  } | null;
  onClose: () => void;
  onSubmit?: (data: SupplierGroupFormattedData) => void;
  selectedObj?: Group | null;
  updateSupplierGroup?: (uuid: string, data: SupplierGroupFormattedData) => Promise<Group | undefined>;
  addSupplierGroup?: (data: SupplierGroupFormattedData) => Promise<Group | undefined>;
  onAddButtonClick?: () => void;
  onEditButtonClick?: () => void;
  onDeleteButtonClick?: () => void;
  onCopyButtonClick?: () => void;
  activeGroupType?: GroupType | null;
  groups?: Group[]; // Danh sách nhóm để kiểm tra trùng mã
}
const SupplierGroupDialog = ({
  open,
  mode,
  initialData,
  onClose,
  onSubmit,
  selectedObj,
  updateSupplierGroup,
  addSupplierGroup,
  onAddButtonClick,
  onEditButtonClick,
  onDeleteButtonClick,
  onCopyButtonClick,
  activeGroupType,
  groups = [] // Mặc định là mảng rỗng
}: SupplierGroupDialogProps) => {
  const [showDuplicateCodeDialog, setShowDuplicateCodeDialog] = useState(false);
  const handleSubmit = async (data: SearchFormValues) => {
    const formattedData: SupplierGroupFormattedData = {
      ma_nhom: data.ma_nhom,
      ten_phan_nhom: data.ten_phan_nhom,
      ten2: data.ten2 || null,
      trang_thai: data.trang_thai.toString(),
      loai_nhom: activeGroupType || GroupType.SUPPLIER1
    };

    try {
      // Kiểm tra mã nhóm đã tồn tại chưa khi thêm mới hoặc sao chép
      if (mode === 'add' && addSupplierGroup) {
        // Lấy danh sách nhóm từ props
        const existingGroups = Array.isArray(groups) ? groups : [];
        // Kiểm tra xem mã nhóm đã tồn tại trong danh sách chưa
        const isDuplicate = existingGroups.some(
          (group: Group) => group.ma_nhom === data.ma_nhom && (!selectedObj || group.uuid !== selectedObj.uuid)
        );

        if (isDuplicate) {
          // Hiển thị thông báo lỗi
          setShowDuplicateCodeDialog(true);
          return;
        }

        await addSupplierGroup(formattedData);
        onClose();
      } else if (mode === 'edit' && selectedObj && updateSupplierGroup) {
        // Kiểm tra xem có nhóm nào khác có cùng mã không (trừ nhóm hiện tại)
        const existingGroups = Array.isArray(groups) ? groups : [];
        const isDuplicate = existingGroups.some(
          (group: Group) => group.ma_nhom === data.ma_nhom && group.uuid !== selectedObj.uuid
        );

        if (isDuplicate) {
          // Hiển thị thông báo lỗi
          setShowDuplicateCodeDialog(true);
          return;
        }

        // Gọi API cập nhật và đợi kết quả
        const result = await updateSupplierGroup(selectedObj.uuid, formattedData);

        // Chỉ đóng dialog khi cập nhật thành công
        if (result) {
          // Thêm một khoảng thời gian nhỏ để đảm bảo dữ liệu được cập nhật trước khi đóng dialog
          setTimeout(() => {
            onClose();
          }, 100);
        }
      } else if (onSubmit) {
        onSubmit(formattedData);
        onClose();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };
  const title = mode === 'add' ? 'Mới' : mode === 'edit' ? 'Sửa' : 'Xem';

  return (
    <>
      <DialogV2
        open={open}
        onClose={onClose}
        title={title}
        maxWidth='lg'
        disableBackdropClose={true}
        disableEscapeKeyDown={true}
        titleIcon={<AritoIcon icon={12} />}
      >
        <AritoFormV2
          mode={mode}
          hasAritoActionBar={false}
          schema={searchSchema}
          onSubmit={handleSubmit}
          initialData={(initialData || initialValues) as Partial<SearchFormValues>}
          className='w-full md:min-w-[500px] lg:min-w-[600px]'
          headerFields={<SupplierGroupForm mode={mode} />}
          bottomBar={
            <BottomActionBarV2
              mode={mode}
              onAdd={onAddButtonClick}
              onEdit={onEditButtonClick}
              onDelete={onDeleteButtonClick}
              onCopy={onCopyButtonClick}
              onSubmit={() => {}}
              onClose={onClose}
            />
          }
        />
      </DialogV2>

      {/* Dialog hiển thị thông báo lỗi khi mã nhóm đã tồn tại */}
      <DialogV2
        open={showDuplicateCodeDialog}
        onClose={() => setShowDuplicateCodeDialog(false)}
        title='Thông báo'
        maxWidth='sm'
        disableBackdropClose={false}
        disableEscapeKeyDown={false}
        titleIcon={<AritoIcon icon={260} />}
      >
        <div className='p-4'>
          <p className='mb-4 text-center'>Mã nhóm đã tồn tại trong danh mục nhóm</p>
          <div className='flex justify-end'>
            <BottomBarActionButtonV2 icon={884} title='Đồng ý' onClick={() => setShowDuplicateCodeDialog(false)} />
          </div>
        </div>
      </DialogV2>
    </>
  );
};

export default SupplierGroupDialog;
